import { Box, css, Stack } from '@mui/material'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useContext, useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import OrganizerIcon from 'assets/icons/nucleo/18px_laptop-chart-line.svg'
import MenuIcon from 'assets/icons/nucleo/18px_menu.svg'
import NewspaperIcon from 'assets/icons/nucleo/18px_newspaper.svg'
import UserIcon from 'assets/icons/nucleo/18px_user.svg'
import { AuthModalContext } from 'components/Auth/AuthModalContext'
import UserAuthenticatedButton from 'components/Auth/UserAuthenticatedButton'
import DisciplineContent from 'components/Layout/Header/DisciplineContent/DisciplineContent'
import { NavbarStyle } from 'components/Layout/Header/style'
import { LinkButton } from 'components/LinkButton'
import { Button } from 'components/common/Button'
import { Loader, LoaderSize, LoaderVariant } from 'components/common/Loader'
import { themeUtils } from 'styles/theme'
import { CartButton } from './DisciplineContent/MobileDisciplineContent/CartButton'
import {
  ButtonsContainer,
  CatchPhrase,
  CatchPhraseContainer,
  Groupe,
  HeaderContainer,
  HomeHeaderContainer,
  Logo,
  LogoContainer,
  minWidthIcon,
  RaceTypeButton,
  SearchBarContainer
} from './Header.style'
import { SearchBar } from './SearchBar/SearchBar'
import { Settings } from './Settings'

interface HeaderProps {
  allCurrencies: string[]
  rounded?: boolean
}

const Header = ({ allCurrencies, rounded }: HeaderProps) => {
  const { setModalParams } = useContext(AuthModalContext)
  const { t, i18n } = useTranslation(['common', 'home', 'authentication'])

  const session = useSession()

  const [isDisciplineOpen, setDisciplineOpen] = useState(false)
  const router = useRouter()
  const isFullHeader = useMemo(() => router.pathname === '/', [router.pathname])

  return (
    <>
      <HeaderContainer>
        <NavbarStyle $rounded={rounded} $isFullHeader={isFullHeader}>
          <LogoContainer href="/" passHref aria-label="Miles Republic">
            <Logo />
          </LogoContainer>
          {!isFullHeader ? (
            <SearchBarContainer>
              <SearchBar hasTop={rounded} />
            </SearchBarContainer>
          ) : (
            <Box />
          )}
          <ButtonsContainer>
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={0.5}
              position="relative"
            >
              <RaceTypeButton
                isDisciplineOpen={isDisciplineOpen}
                onClick={() => setDisciplineOpen(!isDisciplineOpen)}
                label={t('raceTypes')}
              />
              <DisciplineContent
                isOpen={isDisciplineOpen}
                setOpen={setDisciplineOpen}
                allCurrencies={allCurrencies}
              />
              {i18n.language === 'fr' && (
                <LinkButton
                  prefetch={false}
                  leftIcon={<NewspaperIcon css={minWidthIcon} />}
                  label={t('mag')}
                  aria-label={t('magazineAriaLabel')}
                  href="/mag"
                  passHref
                  css={css`
                    ${themeUtils.hideMd}
                    white-space: nowrap;
                  `}
                  noBackground
                  noBorder
                />
              )}
              <LinkButton
                prefetch={false}
                href="/dashboard"
                label={t('home:organizerSpace')}
                aria-label={t('home:organizerSpaceAriaLabel')}
                noBackground
                noBorder
                isExternal
                css={css`
                  ${themeUtils.hideMd}
                  white-space: nowrap;
                `}
                leftIcon={<OrganizerIcon css={minWidthIcon} />}
              />
            </Stack>

            <Stack flexDirection="row" gap={1.5} alignItems="center">
              <Box css={themeUtils.hideMd}>
                <Settings allCurrencies={allCurrencies} />
              </Box>
              {match(session)
                .with({ status: 'loading' }, () => (
                  <Button
                    icon={
                      <Loader
                        size={LoaderSize.SMALLER}
                        variant={LoaderVariant.PRIMARY}
                      />
                    }
                  />
                ))
                .with({ status: 'unauthenticated' }, () => (
                  <Button
                    aria-label={t('authentication:login.button.submit')}
                    icon={<UserIcon css={minWidthIcon} />}
                    onClick={() =>
                      setModalParams({
                        state: 'SIGNIN',
                        redirectUrl: window.location.pathname
                      })
                    }
                  />
                ))
                .with({ status: 'authenticated' }, () => (
                  <UserAuthenticatedButton />
                ))
                .exhaustive()}
              <CartButton />
              <Box css={themeUtils.hideMinMd}>
                <Button
                  aria-label={t('menu.title')}
                  icon={<MenuIcon css={minWidthIcon} />}
                  onClick={() => setDisciplineOpen(!isDisciplineOpen)}
                />
              </Box>
            </Stack>
          </ButtonsContainer>
        </NavbarStyle>
        {isFullHeader && (
          <HomeHeaderContainer>
            <CatchPhraseContainer>
              <Groupe />
              <CatchPhrase>{t('home:head.nextBib')}</CatchPhrase>
            </CatchPhraseContainer>
            <SearchBar forcedExpanded hasTop={rounded} />
          </HomeHeaderContainer>
        )}
      </HeaderContainer>
    </>
  )
}

export default Header
